import { useMediaQuery } from '@sparkai/hooks'
import { cn, type AppTimezonePreference } from '@sparkai/utils'

import { Notification } from '..'
import { Badge } from '../../Badge'
import { Dot } from '../../Dot'
import { Button } from '../../General/Button'
import { MenuLink } from '../../Navigation/MenuLink'
import { NoDataPlaceholder } from '../../NoDataPlaceholder'
import { Popover } from '../../Popover'
import { NotificationGroupItem } from './NotificationGroup'

export type NotificationModalProps = Readonly<{
  collapsed?: boolean
  groupNotifications: NotificationGroupItem[]
  unreadCount: number
  timezonePreference?: AppTimezonePreference
  onViewAll: () => void
  onRowClick?: (id: string) => void
  active?: boolean
}>

export function NotificationModal({
  collapsed,
  groupNotifications,
  unreadCount,
  timezonePreference,
  onViewAll,
  onRowClick,
  active,
}: NotificationModalProps) {
  const { isMobile } = useMediaQuery()

  const EmptyComponent = () => <></>

  const RenderComponent = ({ close }: { close: () => void }) => (
    <NotificationList
      notifications={groupNotifications}
      timezonePreference={timezonePreference}
      onViewAll={() => {
        onViewAll()
        close()
      }}
      onRowClick={onRowClick}
    />
  )

  return (
    <div className={cn('Notifications mt-auto mb-0', !isMobile && 'w-full')}>
      <Popover
        render={({ close }) => (isMobile ? <EmptyComponent /> : <RenderComponent close={close} />)}
        offsets={{ mainAxis: 8, crossAxis: collapsed ? 4 : 20 }}
        placement='top-start'
        className='z-40'
      >
        <span className={cn('MenuLink_Wrapper', !isMobile && 'block w-full')}>
          <NotificationMenu
            collapsed={Boolean(collapsed) || isMobile}
            unreadCount={unreadCount}
            isMobile={isMobile}
            active={active}
            onViewAll={() => {
              if (isMobile) onViewAll()
            }}
          />
        </span>
      </Popover>
    </div>
  )
}

const NotificationMenu = ({
  collapsed,
  unreadCount,
  isMobile,
  onViewAll,
  active,
}: {
  collapsed: boolean
  unreadCount: number
  isMobile: boolean
  onViewAll: () => void
  active?: boolean
}) => {
  return (
    <MenuLink
      data-testid='notification-modal-container'
      className={cn('pb-0', {
        'p-0': isMobile,
      })}
      active={active}
      isMobile={isMobile}
      collapsedIconProps={{
        className: cn({
          'w-6 text-gray-500': isMobile && !active,
          'w-6 text-blue-400': isMobile && active,
        }),
      }}
      labelProps={{ className: active ? 'text-blue-500' : undefined }}
      collapsed={collapsed}
      label={'Notifications'}
      icon={'bell-outline'}
      onClick={onViewAll}
      dotIconAddon={
        unreadCount > 0 ? (
          <Dot
            data-testid='notification-modal-unread-dot'
            className={cn({
              'top-0 -right-[2px]': !collapsed,
              'top-[1px] -right-[1px]': collapsed,
              'bg-blue-500': active,
              'bg-red-400': !active,
            })}
          />
        ) : null
      }
      rightAddon={
        <>
          {unreadCount > 0 && (
            <Badge
              size='small'
              className={cn({
                'text-gray-900': !active,
                'bg-blue-500 text-white': active,
              })}
              data-testid='notification-modal-unread-counter'
            >
              {unreadCount}
            </Badge>
          )}
        </>
      }
      variant={'primary'}
    />
  )
}

const NotificationList = ({
  notifications,
  timezonePreference,
  onViewAll,
  onRowClick,
}: {
  notifications: NotificationGroupItem[]
  timezonePreference?: AppTimezonePreference
  onViewAll: () => void
  onRowClick?: (id: string) => void
}) => {
  return (
    <div
      data-testid='notification-modal-list'
      className='flex w-[420px] flex-col rounded-md bg-white pb-3 font-inter text-sm shadow-xl ring-1 ring-black/5'
    >
      <div className='no-scrollbar max-h-[250px] overflow-x-hidden overflow-y-auto'>
        {notifications.length > 0 && (
          <Notification.Group
            size='small'
            list={notifications}
            timezonePreference={timezonePreference}
            onRowClick={onRowClick}
          />
        )}
        <div className='flex justify-center'>
          <NoDataPlaceholder
            variant='xs'
            title=''
            containerClassName='mt-[28px]'
            text={`You're up to date!`}
            data-testid='notification-modal-updated-message'
          />
        </div>
      </div>
      <div className='w-full px-3'>
        {notifications.length > 0 && (
          <Button
            data-testid='notification-modal-view-all'
            className='mt-3 w-fit'
            variant='white'
            onClick={onViewAll}
          >
            View All
          </Button>
        )}
      </div>
    </div>
  )
}
